import { useQuery, useMutation } from '@tanstack/react-query'
import QRCode from 'qrcode'
import { getInstanceType } from 'app/lib/utils'
import { hardNavigateWithViewTransition } from 'shared/viewTransition'

let baseUrl = ''

switch (getInstanceType()) {
  case 'prd':
    baseUrl = 'https://www.techferral.com'
    break
  case 'dev':
    baseUrl = 'https://dev.techferral.com'
    break
  default:
    baseUrl = 'http://localhost:3000'
}

async function getCurrentUser() {
  const response = await fetch('/api/users/currentUser')
  const user = ((await response.json()) as CurrentUser)!

  const promises = user.organizations.map(async (org) => {
    if (org.referralCode) {
      const url = `${baseUrl}/r/${org.referralCode}`
      org.qrCode = await QRCode.toDataURL(url)
    }
  })

  await Promise.all(promises)

  return user as CurrentUser
}

export async function logout() {
  await fetch('/auth/logout', {
    method: 'POST',
  })
  hardNavigateWithViewTransition('/')
}

export async function generateEmailVerification(email: string) {
  const response = await fetch('/api/users/email-verification', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  })

  if (!response.ok) {
    throw new Error('Error generating email verification')
  }

  return (await response.json())!
}

export async function verifyEmail(code: string) {
  const response = await fetch(`/api/users/email-verification/${code}`, {
    method: 'POST',
  })

  if (!response.ok) {
    throw new Error('Error verifying email')
  }

  return (await response.json())!
}

export async function isUsernameAvailable(username: string): Promise<boolean> {
  const response = await fetch(`/auth/isAvailableUsername/${username}`)

  if (!response.ok) {
    throw new Error('Error checking username availability')
  }

  const data = await response.text()
  return data === 'true'
}

export const useCurrentUserQuery = {
  queryKey: ['currentUser'],
  queryFn: getCurrentUser,
}

export const useCurrentUser = () => useQuery(useCurrentUserQuery)

export const useGenerateEmailVerificationMutation = () => {
  return useMutation({
    mutationFn: ({ email }: { email: string }) =>
      generateEmailVerification(email),
  })
}

export const useVerifyEmailMutation = () => {
  return useMutation({
    mutationFn: ({ code }: { code: string }) => verifyEmail(code),
  })
}
